import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import useOverlayHistory from 'services/useOverlayHistory';
import ConfirmModal from '../moduleComponents/ConfirmModal';
import ViewProjectsList from './viewProjectsList';
import ViewProjectsBoard from './viewProjectsBoard';
import ModalProjectForm from './viewComponents/ModalProjectForm';
import { listProjects, createProject, updateProject, deleteProject } from '../moduleServices/planner_projects_api';

export default function MainProjects({ onCanvasMaxWidthChange }) {
  const [activePane, setActivePane] = useState('list');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectModal, setProjectModal] = useState({ open: false, project: null });
  const [projectSubmitting, setProjectSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const requestCloseDelete = useOverlayHistory(!!deleteTarget, () => setDeleteTarget(null), {
    stateKey: 'qappProjectDelete',
  });

  const refreshProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProjects();
      setProjects(Array.isArray(data) ? data : data?.results || []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load projects.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const handleOpenBoard = (project) => {
    setSelectedProject(project);
    setActivePane('board');
  };

  const handleBackToList = () => {
    setSelectedProject(null);
    setActivePane('list');
  };

  const submitProject = async ({ name, description }) => {
    setProjectSubmitting(true);
    try {
      if (projectModal.project) {
        const updated = await updateProject(projectModal.project.id, { name, description });
        toast.success('Project updated');
        setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
        setSelectedProject((sp) => (sp && sp.id === updated.id ? { ...sp, ...updated } : sp));
      } else {
        const created = await createProject({ name, description });
        toast.success('Project created');
        setProjects((prev) => [created, ...prev]);
      }
      setProjectModal({ open: false, project: null });
    } catch (e) {
      console.error(e);
      toast.error('Could not save project.');
    } finally {
      setProjectSubmitting(false);
    }
  };

  const confirmDeleteProject = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget.id);
      toast.success('Project deleted');
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      if (selectedProject?.id === deleteTarget.id) {
        setSelectedProject(null);
        setActivePane('list');
      }
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
      toast.error('Could not delete project.');
    }
  };

  return (
    <div>
      {activePane === 'list' && (
        <ViewProjectsList
          projects={projects}
          loading={loading}
          onOpenBoard={handleOpenBoard}
          onCreateClick={() => setProjectModal({ open: true, project: null })}
          onEditClick={(project) => setProjectModal({ open: true, project })}
          onDeleteClick={(project) => setDeleteTarget(project)}
        />
      )}

      {activePane === 'board' && selectedProject && (
        <ViewProjectsBoard
          projectSummary={selectedProject}
          onBack={handleBackToList}
          onCanvasMaxWidthChange={onCanvasMaxWidthChange}
        />
      )}

      <ModalProjectForm
        isOpen={projectModal.open}
        onClose={() => setProjectModal({ open: false, project: null })}
        onSubmit={submitProject}
        initial={projectModal.project}
        submitting={projectSubmitting}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={requestCloseDelete}
        onConfirm={confirmDeleteProject}
        title="Delete project"
        message={`Delete "${deleteTarget?.name}" and its entire board (columns and cards)?`}
        confirmText="Delete"
      />
    </div>
  );
}
